import { useSuspenseQuery } from "@tanstack/react-query";

import { TrendBadge } from "@/components/Badge/TrendBadge";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { NoData } from "@/components/NoData/NoData";
import { Speedometer } from "@/components/Speedometer/Speedometer";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { canListAnySurvey } from "@/helpers/permissions";
import { isDefined } from "@/helpers/util";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function FeelingAtHomeCard(): React.ReactNode {
  const slug = useSlug();
  const { t, i18n } = useTranslation();
  const hasPermission = usePermission();

  const analyticsQueries = useAnalyticsQueries();
  const { data } = useSuspenseQuery(analyticsQueries.fah());

  const noData = data?.totalFeelingAtHomeScore?.rating === undefined;

  return (
    <AnalyticsCard.Root>
      <AnalyticsCard.Header
        title={t("page.analytics-overview.chart.feeling-at-home.title")}
        tooltip={t("page.analytics-overview.chart.feeling-at-home.new-tooltip")}
      />
      {noData ? (
        <NoData />
      ) : (
        <div className="grid grid-cols-1 items-center gap-space-xl @xl:grid-cols-2">
          <Stack gap="xl" align="center">
            <Inline gap="md" align="center">
              <Speedometer
                score={(data?.totalFeelingAtHomeScore?.rating || 0) / 10}
                renderCurrentValue={(currScore) => (currScore ? (currScore * 10).toFixed(1) : undefined)}
              />
            </Inline>

            <section className="flex w-full gap-space-md">
              <article className="flex min-w-control-narrow flex-1 flex-col gap-space-xs rounded-md border border-border-default p-space-md">
                <span className="text-caption text-text-secondary">
                  {data?.latestMonthlyFeelingAtHomeScore
                    ? t("page.analytics-overview.chart.feeling-at-home.last-month", {
                        month: formatDate(i18n, "month", data.latestMonthlyFeelingAtHomeScore.month),
                      })
                    : "-"}
                </span>
                <Inline gap="sm" align="center">
                  <span className="text-figure-md text-text-brand-secondary">
                    {data.latestMonthlyFeelingAtHomeScore?.rating.toFixed(1) || "-"}
                  </span>
                  {isDefined(data.latestMonthlyFeelingAtHomeScore) && isDefined(data?.totalFeelingAtHomeScore) && (
                    <TrendBadge
                      value={data.latestMonthlyFeelingAtHomeScore.rating || 0}
                      referenceValue={data.totalFeelingAtHomeScore.rating}
                    />
                  )}
                </Inline>
                {data.hasFeelAtHomeDetailsLink && hasPermission(canListAnySurvey) && (
                  <Button
                    type="link"
                    href={
                      routes.surveys.fah({ slug }) +
                      (data.latestMonthlyFeelingAtHomeScore
                        ? `?month=${data.latestMonthlyFeelingAtHomeScore.month.substring(0, 7)}`
                        : "")
                    }
                    variant="ghostPrimary"
                  >
                    {t("page.analytics-overview.chart.feeling-at-home.view-details")}
                  </Button>
                )}
              </article>
              <article className="flex min-w-control-narrow flex-1 flex-col gap-space-xs rounded-md border border-border-default p-space-md">
                <span className="text-caption text-text-secondary">
                  {t("page.analytics-overview.chart.feeling-at-home.benchmark")}
                </span>
                <Inline gap="sm" align="center">
                  <span className="text-figure-md text-text-brand-secondary">
                    {data.benchmarkFeelingAtHomeScore?.rating.toFixed(1) || "-"}
                  </span>
                </Inline>
              </article>
            </section>
          </Stack>
          <Stack gap="md">
            <span className="text-caption-emphasis">
              {t("page.analytics-overview.chart.feeling-at-home.range.title")}
            </span>
            <Inline gap="sm" align="center" wrap={false}>
              <span className="flex w-control-xl shrink-0 items-center justify-center rounded-md bg-chart-score-very-low py-space-xs text-body-emphasis whitespace-nowrap text-text-default-inverse">
                0-2
              </span>
              <span className="min-w-0 text-caption">{t("page.analytics-overview.chart.feeling-at-home.range.1")}</span>
            </Inline>
            <Inline gap="sm" align="center" wrap={false}>
              <span className="flex w-control-xl shrink-0 items-center justify-center rounded-md bg-chart-score-low py-space-xs text-body-emphasis whitespace-nowrap text-text-default-inverse">
                2-4
              </span>
              <span className="min-w-0 text-caption">{t("page.analytics-overview.chart.feeling-at-home.range.2")}</span>
            </Inline>
            <Inline gap="sm" align="center" wrap={false}>
              <span className="flex w-control-xl shrink-0 items-center justify-center rounded-md bg-chart-score-midpoint py-space-xs text-body-emphasis whitespace-nowrap text-text-primary">
                4-6
              </span>
              <span className="min-w-0 text-caption">{t("page.analytics-overview.chart.feeling-at-home.range.3")}</span>
            </Inline>
            <Inline gap="sm" align="center" wrap={false}>
              <span className="flex w-control-xl shrink-0 items-center justify-center rounded-md bg-chart-score-high py-space-xs text-body-emphasis whitespace-nowrap text-text-primary">
                6-8
              </span>
              <span className="min-w-0 text-caption">{t("page.analytics-overview.chart.feeling-at-home.range.4")}</span>
            </Inline>
            <Inline gap="sm" align="center" wrap={false}>
              <span className="flex w-control-xl shrink-0 items-center justify-center rounded-md bg-chart-score-very-high py-space-xs text-body-emphasis whitespace-nowrap text-text-default-inverse">
                8-10
              </span>
              <span className="min-w-0 text-caption">{t("page.analytics-overview.chart.feeling-at-home.range.5")}</span>
            </Inline>
          </Stack>
        </div>
      )}
    </AnalyticsCard.Root>
  );
}
